// Money is sent to the server as a decimal string, never a float.
export const MONEY_FIELD_ERROR = 'Enter an amount like 1234.56';
export const MONEY_NEGATIVE_ERROR = 'Enter an amount of zero or more, like 1234.56';

const PLAIN = /^-?\d{1,13}(\.\d{1,2})?$/;
// Commas only as thousands separators: "1,234.56" yes, "1,5" and "1.234,56" no.
const GROUPED = /^\d{1,3}(,\d{3})+(\.\d{1,2})?$/;

// Returns { value } with a string matching ^-?\d{1,13}(\.\d{1,2})?$, or
// value null for an empty field (leave it out of the request), or { error }.
export function parseMoneyInput(input, { allowNegative = false } = {}) {
  if (input === undefined || input === null) return { value: null };
  let text = String(input).trim();
  if (text === '') return { value: null };

  let negative = false;
  if (text.startsWith('-')) {
    negative = true;
    text = text.slice(1).trim();
  }
  text = text.replace(/^\$\s*/, '');
  if (text.startsWith('-') && !negative) {
    negative = true;
    text = text.slice(1).trim();
  }

  if (GROUPED.test(text)) text = text.replace(/,/g, '');
  if (!PLAIN.test(text) || text.startsWith('-')) return { error: MONEY_FIELD_ERROR };

  const isZero = !/[1-9]/.test(text);
  if (negative && !isZero && !allowNegative) return { error: MONEY_NEGATIVE_ERROR };
  return { value: negative && !isZero ? `-${text}` : text };
}

// String form, or null when the field is empty. Throws on invalid input so
// callers that already validated can't send a bad value by accident.
export function toMoneyString(input, options) {
  const { value, error } = parseMoneyInput(input, options);
  if (error) throw new Error(error);
  return value;
}

// A liability balance is stored negative: "1234.56" -> "-1234.56".
export function negateMoneyString(value) {
  if (value === null || value === undefined) return value;
  if (!/[1-9]/.test(value)) return value.replace(/^-/, '');
  return value.startsWith('-') ? value.slice(1) : `-${value}`;
}

// Display: $12,345.67, -$1,234.56. Currency defaults to USD; pass an ISO
// code (e.g. an account's currency_code) when one is known.
export function formatMoney(amount, currency, { plus = false } = {}) {
  const n = Number(amount);
  if (amount === null || amount === undefined || amount === '' || !Number.isFinite(n)) return '';
  const opts = { style: 'currency', minimumFractionDigits: 2, maximumFractionDigits: 2 };
  let f;
  try { f = new Intl.NumberFormat('en-US', { ...opts, currency: String(currency || 'USD').toUpperCase() }); }
  catch (e) { f = new Intl.NumberFormat('en-US', { ...opts, currency: 'USD' }); }
  const body = f.format(Math.abs(n));
  if (n < 0 && body !== f.format(0)) return `-${body}`;
  return (plus && n > 0 ? '+' : '') + body;
}
